/* eslint-disable @typescript-eslint/ban-ts-comment */
import { TableTemplate } from '@repo/types';
import {
  ColumnDef,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
} from '@tanstack/react-table';
import {
  CSSProperties,
  ForwardedRef,
  forwardRef,
  ReactNode,
  UIEvent,
  useMemo,
} from 'react';
import { AppTableProvider } from './lib/context/use-app-table-context';
import { AppTableNewRef } from './types/table-props';
import { DefaultTable } from './ui/default-table';
import { VirtualTable } from './ui/virtual-table';
export type { AppTableNewRef } from './types/table-props';

export interface AppTableNewProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  className?: string;
  rowClassName?: string;
  rowHeight?: number;
  isHeaderSticky?: boolean;
  calculatedRowClassName?: (row: TData, additionalProps?: Row<TData>) => string;
  calculatedRowStyle?: (
    row: TData,
    additionalProps?: Row<TData>,
  ) => CSSProperties;
  presentationType?: 'default' | 'virtual';
  isLoading?: boolean;
  isSkeleton?: boolean;
  skeletonRowCount?: number;
  isResizable?: boolean;
  isDraggable?: boolean;
  rowSelection?: RowSelectionState;
  hiddenColumns?: string[];
  disablePagination?: boolean;
  disableToolbar?: boolean;
  enableColumnFilter?: boolean;
  enableFooter?: boolean;
  absoluteColumnWidth?: boolean;
  columnPinning?: {
    left?: string[];
    right?: string[];
  };
  customSelectColumn?: boolean;
  //Templates
  isTemplate?: boolean;
  templates?: TableTemplate[];
  selectedTemplateId?: number | null;
  onSelectTemplate?: (template: TableTemplate) => void;
  onDeleteTemplate?: (template: TableTemplate) => void;
  //
  setRowSelection?: (rowSelection: RowSelectionState) => void;
  defaultSorting?: SortingState;
  pagination?: PaginationState;
  pageSizeOptions?: number[];
  sorting?: SortingState;
  totalRows?: number;
  manualPagination?: boolean;
  onPaginationChange?: (pagination: PaginationState) => void;
  onSortingChange?: (sorting: SortingState) => void;
  onAddNew?: () => void;
  onImport?: (file: File) => void;
  onExcel?: (columns: string[], rows: Partial<TData>[]) => void;
  onPdf?: (columns: string[], rows: Partial<TData>[]) => void;
  onDelete?: () => void;
  onDoubleClickRow?: (row: TData) => void;
  onClickRow?: (row: TData) => void;
  getRowId?: (row: TData) => string;
  CustomToolbar?: ReactNode;
  CustomFilter?: ReactNode;
  onSearchChange?: (search: string) => void;
  onTableScroll?: (event: UIEvent<HTMLDivElement>) => void;
  isFetchingMore?: boolean;
  fetchingMoreRowCount?: number;
}

const AppTableNewComponent = <TData,>(
  {
    presentationType = 'default',
    isResizable = true,
    disablePagination = false,
    disableToolbar = false,
    ...props
  }: AppTableNewProps<TData>,
  ref: ForwardedRef<AppTableNewRef<TData>>,
) => {
  const TableComponent = useMemo(() => {
    if (presentationType === 'default') return DefaultTable;
    return VirtualTable;
  }, [presentationType]);

  return (
    <AppTableProvider<TData>
      disablePagination={disablePagination}
      disableToolbar={disableToolbar}
      isResizable={isResizable}
      {...props}
    >
      {/* @ts-expect-error */}
      <TableComponent ref={ref} />
    </AppTableProvider>
  );
};

export const AppTableNew = forwardRef(AppTableNewComponent) as <TData>(
  props: AppTableNewProps<TData> & {
    ref?: ForwardedRef<AppTableNewRef<TData>>;
  },
) => ReturnType<typeof AppTableNewComponent>;
